/*!
 * Created by netlogix GmbH & Co. KG
 *
 * @copyright netlogix GmbH & Co. KG
 */

import { Component, Inject, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { State } from '@core/store';
import * as DepartmentsActions from '@departments/store/departments.actions';
import {
  getCurrentDepartment,
  getIsDepartmentActionPending,
  getNextDepartmentSortOrder
} from '@departments/store/departments.selectors';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Department } from '@shared/models';
import { departmentTint, invalidFieldsHint } from '@shared/utils';
import { Observable } from 'rxjs';
import { take, tap } from 'rxjs/operators';

export const DEFAULT_DEPARTMENT_COLOR = '#BDBDBD';
export const DEFAULT_DEPARTMENT_OPACITY = 8;

export function holdsAtLeastOneKind(form: AbstractControl): ValidationErrors | null {
  return form.value.canHoldDrivers || form.value.canHoldVehicles
    ? null
    : { noKind: true };
}

/** Formularfeld zu Beschriftung - fuer den Hinweis am gesperrten Speichern-Button. */
const FIELD_LABELS: Record<string, string> = {
  name: 'departments.name',
  sortOrder: 'departments.sortOrder',
  color: 'departments.color',
  opacity: 'departments.opacity'
};

@Component({
    selector: 'app-department-detail',
    templateUrl: './department-detail.component.html',
    styleUrls: ['./department-detail.component.scss'],
    standalone: false
})
export class DepartmentDetailComponent implements OnInit {
  department$: Observable<Department>;
  isActionPending: Observable<boolean>;

  departmentForm = new UntypedFormGroup({
    name: new UntypedFormControl('', Validators.required),
    sortOrder: new UntypedFormControl(null, [Validators.required, Validators.min(1)]),
    color: new UntypedFormControl(DEFAULT_DEPARTMENT_COLOR, [
      Validators.required,
      Validators.pattern(/^#[0-9A-Fa-f]{6}$/)
    ]),
    opacity: new UntypedFormControl(DEFAULT_DEPARTMENT_OPACITY, [
      Validators.required,
      Validators.min(0),
      Validators.max(100)
    ]),
    canHoldDrivers: new UntypedFormControl(true),
    canHoldVehicles: new UntypedFormControl(false)
  }, holdsAtLeastOneKind);

  constructor(
    private store: Store<State>,
    private translateService: TranslateService,
    @Inject(MAT_DIALOG_DATA) public parameters: { departmentId?: string }
  ) { }

  /** Erklaert am Tooltip, warum der Speichern-Button gesperrt ist. */
  get persistHint(): string {
    const fieldHint = invalidFieldsHint(
      this.departmentForm,
      FIELD_LABELS,
      (key, params) => this.translateService.instant(key, params)
    );

    if (!this.departmentForm.hasError('noKind')) {
      return fieldHint;
    }

    const kindHint = this.translateService.instant('departments.holdsRequired');

    return fieldHint ? `${fieldHint} ${kindHint}` : kindHint;
  }

  /** Hinterlegung der Vorschau: genau der Wert, den die Kalenderliste spaeter nutzt. */
  get previewTint(): string {
    return departmentTint(this.departmentForm.value.color, this.departmentForm.value.opacity);
  }

  ngOnInit(): void {
    this.store.dispatch(new DepartmentsActions.LoadDepartment(this.parameters.departmentId));

    this.isActionPending = this.store.select(getIsDepartmentActionPending);
    this.department$ = this.store.select(getCurrentDepartment).pipe(
      tap(department => this.departmentForm.patchValue(department))
    );

    if (!this.parameters.departmentId) {
      // Neue Abteilungen landen standardmaessig am Ende der Reihenfolge.
      this.store.select(getNextDepartmentSortOrder).pipe(take(1)).subscribe(
        sortOrder => this.departmentForm.patchValue({ sortOrder })
      );
    }
  }

  /**
   * Bewusst nur die schreibbaren Felder plus IRI: die Zaehler aus dem Payload sind
   * berechnet und wuerden beim PUT als unbekannte Attribute mitgesendet.
   */
  persistDepartment(department: Department): void {
    // Der Button ist zwar "disabled", bleibt wegen disabledInteractive (fuer den Tooltip)
    // aber klickbar - Material unterdrueckt Klicks nur bei Anchor-Elementen.
    if (this.departmentForm.invalid) {
      return;
    }

    this.store.dispatch(new DepartmentsActions.PersistDepartment({
      '@id': department['@id'],
      ...this.departmentForm.value
    }));
  }
}
